import type { Locale } from '@seek/ca-myprofile-utils';
import { type Button, TextLink } from 'braid-design-system';
import { createElement, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import type {
  AnalyticsLinkEvent,
  AnalyticsLinkPosition,
} from 'src/modules/analytics/types';
import { useAppConfig } from 'src/modules/appConfig';
import { externalLinkClicked } from 'src/reducers/location';

const seekAuDomainRegex = /seek|seeklearning\.com\.au/;

export const getSeekUrl = (href: string, locale: Locale) => {
  if (locale === 'NZ') {
    return href.replace('com.au', 'co.nz');
  }

  return href;
};

export const getHref = (href: string, locale: Locale) =>
  seekAuDomainRegex.test(href) ? getSeekUrl(href, locale) : href;

const getTarget = (href: string) =>
  seekAuDomainRegex.test(href) ? '_self' : '_blank';

interface Props {
  href: string;
  rel?: string;
  onClick?: () => void;
  analytics?: {
    linkPosition: AnalyticsLinkPosition;
    linkName: AnalyticsLinkEvent;
    thirdPartyLink: boolean;
  };
  children?: React.ReactNode;
  target?: string;
  hitArea?: 'standard' | 'large';
  component?:
    | React.ComponentType<{ href: string; children: React.ReactNode }>
    | string;
}

type ExternalLinkProps = Props &
  React.ComponentProps<typeof Button> &
  React.ComponentProps<typeof TextLink>;

export const ExternalLink = ({
  href,
  onClick,
  analytics,
  children,
  target,
  component = TextLink,
  ...restProps
}: ExternalLinkProps) => {
  const dispatch = useDispatch();
  const { locale } = useAppConfig();

  const onLinkClick = useCallback(() => {
    if (onClick) {
      onClick();
    }

    if (analytics) {
      dispatch(externalLinkClicked({ analytics, type: 'e' }));
    }
  }, [onClick, analytics, dispatch]);

  const linkHref = getHref(href, locale);

  const props = {
    children,
    target: target || getTarget(linkHref),
    href: linkHref,
    onClick: analytics || onClick ? onLinkClick : undefined,
    ...restProps,
  };

  return createElement(component, props, children);
};
